import React, { useState, useEffect } from 'react';
import { FiMap, FiUsers } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseUrl from '../../../Url/BaseUrl';

const EditCityVillage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the city/village data passed from the Manage page
  const { cityVillage } = location.state || {};
  
  // Form state, pre-filled with the received data
  const [formData, setFormData] = useState({
    cityVillageCode: '',
    cityVillageNameEn: '',    // English name
    cityVillageNameHi: '',    // Hindi name
    districtCode: '',
    status: true
  });

  // State for districts dropdown
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with the received data and fetch districts
  useEffect(() => {
    if (cityVillage) {
      setFormData({
        cityVillageCode: cityVillage.cityVillageCode || '',
        cityVillageNameEn: cityVillage.cityVillageName || '',
        cityVillageNameHi: cityVillage.hindi || '',
        districtCode: cityVillage.districtCode || '',
        status: cityVillage.status || true
      });
    }

    // Fetch districts for the dropdown
    const fetchDistricts = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BaseUrl.baseurl}Districts`, {
          method: 'GET',
          headers: { 'accept': 'text/plain' }
        });
        
        if (!response.ok) throw new Error(t('httpError', { status: response.status }));
        const data = await response.json();
        setDistricts(data);
      } catch (error) {
        console.error('Error fetching districts:', error);
        setError(t('failedToLoadDistricts'));
      } finally {
        setLoading(false);
      }
    };
    fetchDistricts();
  }, [cityVillage, t]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Prepare the payload matching the API schema
      const payload = {
        cityVillageCode: formData.cityVillageCode,
        cityVillageName: formData.cityVillageNameEn,   // English name
        hindi: formData.cityVillageNameHi.trim() || null, // Hindi name
        districtCode: formData.districtCode,
        district: null,
        status: formData.status
      };
      
      // Make the PUT request to update the record
      const response = await fetch(`${BaseUrl.baseurl}CityVillages/${formData.cityVillageCode}`, {
        method: 'PUT',
        headers: {
          'accept': 'text/plain',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || t('updateFailedStatus', { status: response.status }));
      }

      alert(t('updateSuccess'));
      navigate('/route-finder-panel');
      
    } catch (error) {
      console.error('Error updating record:', error);
      setError(t('failedToUpdate'));
    } finally {
      setSubmitting(false);
    }
  };

  // If no data was passed, show an error message
  if (!cityVillage) {
    return (
      <div className="add-region-container">
        <div className="error-message">
          <p>{t('noDataProvided')}</p>
          <button onClick={() => navigate(-1)}>{t('goBack')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-region-container">
      <div className="form-header">
        <button 
          className="btn-back" 
          onClick={() => navigate(-1)}
          disabled={submitting}
        >
          ← {t('back')}
        </button>
        <h2>{t('editCityVillageTitle')}</h2>
        <p>{t('editDetailsFor', { name: cityVillage.cityVillageName })}</p>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="region-form">
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">
              <FiMap className="section-icon" />
              {t('cityVillageInformation')}
            </h3>
            
            <div className="input-group">
              <label htmlFor="cityVillageNameEn">{t('cityVillageName')} *</label>
              <input
                type="text"
                id="cityVillageNameEn"
                name="cityVillageNameEn"
                value={formData.cityVillageNameEn}
                onChange={handleInputChange}
                required
                disabled={submitting}
                maxLength="100"
              />
            </div>

            <div className="input-group">
              <label htmlFor="cityVillageNameHi">{t('hindiName')}</label>
              <input
                type="text"
                id="cityVillageNameHi"
                name="cityVillageNameHi"
                value={formData.cityVillageNameHi}
                onChange={handleInputChange}
                disabled={submitting}
                maxLength="100"
                placeholder={t('hindiNamePlaceholder')}
              />
              <small className="input-hint">{t('hindiNameHint')}</small>
            </div>

            <div className="input-row">
              <div className="input-group">
                <label htmlFor="cityVillageCode">{t('cityVillageCode')}</label>
                <input
                  type="text"
                  id="cityVillageCode"
                  name="cityVillageCode"
                  value={formData.cityVillageCode}
                  disabled={true}
                  className="disabled-field"
                />
                <small className="input-hint">{t('uniqueIdentifierHint')}</small>
              </div>

              <div className="input-group">
                <label htmlFor="districtCode">{t('district')} *</label>
                <select
                  id="districtCode"
                  name="districtCode"
                  value={formData.districtCode}
                  onChange={handleInputChange}
                  required
                  disabled={loading || submitting}
                >
                  <option value="">{t('selectDistrict')}</option>
                  {districts.map(district => (
                    <option key={district.districtCode} value={district.districtCode}>
                      {district.districtName} ({district.districtCode})
                    </option>
                  ))}
                </select>
                {loading && <small className="input-hint">{t('loadingDistricts')}</small>}
              </div>
            </div>
          </div>

          {/* Optional status section – kept but commented out as in other files
          <div className="form-section">
            <h3 className="section-title">
              <FiUsers className="section-icon" />
              {t('status')}
            </h3>
            <div className="input-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t('active')}</span>
              </label>
            </div>
          </div>
          */}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || loading}
          >
            {submitting ? t('updating') : t('updateCityVillage')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCityVillage;